import type * as React from "react";

/**
 * The app-wide layout container (RAMPS-4444).
 *
 * ramps-ui runs mostly inside partner mobile webviews, where screen size is
 * naturally contained, so max-width had drifted: most routes hand-rolled their
 * own `mx-auto ... max-w-screen-sm` shell and several sections (the locator/map
 * screen most visibly) had no cap at all and stretched edge to edge on desktop.
 * This component is now the single place the page width is applied. Routes must
 * NOT re-declare `mx-auto max-w-*` on their own shells.
 *
 * The width itself lives in `--container-app` in styles.css (→ `max-w-app`).
 *
 * Past both the width and height caps this element also becomes the desktop
 * card — capped height, rounded corners, drop shadow. That is applied by a
 * media query in styles.css keyed off `[data-slot="app-shell"]`, not by classes
 * here, because it has to re-base the route shells' viewport-height units at the
 * same time. Keep the data-slot attribute.
 *
 * Keep this a plain block element:
 *
 * - No `overflow-*`, `transform`, `filter`, `contain` or `will-change`. Each of
 *   those makes this element the containing block for `position: fixed`
 *   descendants — and since this element is only as tall as its content, the
 *   fixed bottom bars would then pin to the bottom of the *page* rather than the
 *   viewport. `overflow-*` additionally creates a stray scroll container that
 *   clips the map screen's scroll region.
 * - No `flex` or `h-*`. Route shells set their own definite height in viewport
 *   units (`h-dvh` / `min-h-dvh`) and anchor flex chains off it; making them
 *   flex items here would expose them to stretch/shrink interactions.
 */
export function AppShell({ children }: { children: React.ReactNode }) {
	return (
		<div data-slot="app-shell" className="mx-auto w-full max-w-app">
			{children}
		</div>
	);
}
